import { first, isEqual } from 'lodash';
import * as moment from 'moment-timezone';
import { Moment } from 'moment-timezone';
import { Observable, Subject } from 'rxjs';
import { map } from 'rxjs/operators';
import { uid } from '../../../helpers';
import { shareReplayHot, snapshot } from '../../../rxjs';
import { serialise } from '../../../serialisers/primitive-object-serialiser';
import { WithRef } from '../../interfaces';
import {
  CollectionReference,
  DocumentReference,
  Query,
  QueryConstraint,
} from '../adaptor';
import { Firestore } from '../firestore';

export interface IBaseCacheable {
  lastAccessed: Moment;
  destroy$: Subject<void>;
}

interface ICacheItem<T> extends IBaseCacheable {
  value$: Observable<WithRef<T>>;
}

export class CacheLimiter<T extends IBaseCacheable> {
  private _ttlMs?: number;
  private _accessOrder: Map<string, void> = new Map();

  constructor(private _limit?: number) {}

  setTTL(ttlMs: number): void {
    this._ttlMs = ttlMs;
  }

  isExpiredItem(item?: T): boolean {
    const now = moment();
    return (
      !!this._ttlMs &&
      !!item &&
      Math.abs(now.diff(item.lastAccessed, 'milliseconds')) > this._ttlMs
    );
  }

  markAccessed(items: Map<string, T>, recentlyUsedPath: string): void {
    const item = items.get(recentlyUsedPath);
    if (item) {
      items.set(recentlyUsedPath, {
        ...item,
        lastAccessed: moment(),
      });
    }

    if (this._limit) {
      this._markAsRecentlyUsed(recentlyUsedPath);

      if (items.size > this._limit) {
        this._evictLeastRecentlyUsed(items);
      }
    }
  }

  pruneExpired(items: Map<string, T>): void {
    if (!this._ttlMs) {
      return;
    }

    const now = moment();
    for (const [path, item] of items) {
      const diffMs = Math.abs(now.diff(item.lastAccessed, 'milliseconds'));
      if (diffMs > this._ttlMs) {
        items.get(path)?.destroy$.next();
        items.delete(path);
        this._accessOrder.delete(path);
      }
    }
  }

  private _markAsRecentlyUsed(path: string): void {
    this._accessOrder.delete(path);
    this._accessOrder.set(path, undefined);
  }

  private _evictLeastRecentlyUsed(items: Map<string, unknown>): void {
    const leastRecentlyUsedKey = String(this._accessOrder.keys().next().value);
    if (leastRecentlyUsedKey) {
      items.delete(leastRecentlyUsedKey);
      this._accessOrder.delete(leastRecentlyUsedKey);
    }
  }
}

export class FirestoreCache<T extends object> {
  private _cacheLimiter: CacheLimiter<ICacheItem<T>>;
  private _items: Map<string, ICacheItem<T>> = new Map();

  constructor(
    limit?: number,
    private _cacheEnabled: boolean = false
  ) {
    this._cacheLimiter = new CacheLimiter<ICacheItem<T>>(limit);
  }

  setTTL(ttlMs: number): void {
    this._cacheLimiter.setTTL(ttlMs);
  }

  doc$(ref: DocumentReference<T>): Observable<WithRef<T>> {
    const path = ref.path;
    if (!this._cacheEnabled) {
      return Firestore.doc$(ref);
    }

    const cachedItem = this._items.get(path);

    if (!cachedItem) {
      const destroy$ = new Subject<void>();
      this._items.set(path, {
        lastAccessed: moment(),
        destroy$,
        value$: Firestore.doc$(ref).pipe(shareReplayHot(destroy$)),
      });
    }

    this._cacheLimiter.markAccessed(this._items, path);
    this._cacheLimiter.pruneExpired(this._items);

    return (this._items.get(path) as ICacheItem<T>).value$;
  }

  getDoc(ref: DocumentReference<T>): Promise<WithRef<T>> {
    return snapshot(this.doc$(ref));
  }

  setCacheEnabled(cacheEnabled: boolean): void {
    this._cacheEnabled = cacheEnabled;
  }
}

export interface ICacheQueryObject<T extends object> extends IBaseCacheable {
  properties: object;
  results$: Observable<WithRef<T>[]>;
}

export class FirestoreQueryCache<T extends object> {
  private _cacheLimiter: CacheLimiter<ICacheQueryObject<T>>;
  private _queries: Map<string, ICacheQueryObject<T>> = new Map();

  constructor(
    private _onDestroy$: Observable<void> = AppDestroy.onDestroy$,
    private _cacheEnabled: boolean = false
  ) {
    this._cacheLimiter = new CacheLimiter<ICacheQueryObject<T>>();
  }

  setTTL(ttlMs: number): void {
    this._cacheLimiter.setTTL(ttlMs);
  }

  query$(
    properties: object,
    col: CollectionReference<T> | Query<T> | string,
    ...queryConstraints: QueryConstraint[]
  ): Observable<WithRef<T>[]> {
    const serialisedProperties = serialise(properties);
    const newQuery$ = Firestore.query$(col, ...queryConstraints);
    if (!this._cacheEnabled) {
      return newQuery$;
    }

    const existingQuery = this._findExistingQuery(serialisedProperties);
    this._cacheLimiter.pruneExpired(this._queries);

    if (existingQuery) {
      return existingQuery.results$;
    }

    const results$ = newQuery$.pipe(shareReplayHot(this._onDestroy$));

    const destroy$ = new Subject<void>();
    const query = {
      lastAccessed: moment(),
      properties: serialisedProperties,
      destroy$,
      results$,
    };

    this._queries.set(uid(), query);

    return results$;
  }

  find$(
    properties: object,
    col: CollectionReference<T> | Query<T> | string,
    ...queryConstraints: QueryConstraint[]
  ): Observable<WithRef<T> | undefined> {
    return this.query$(properties, col, ...queryConstraints).pipe(
      map((items) => first(items))
    );
  }

  setCacheEnabled(enabled: boolean): void {
    this._cacheEnabled = enabled;
  }

  private _findExistingQuery(
    serialisedProperties: object
  ): ICacheQueryObject<T> | undefined {
    for (const [key, query] of this._queries) {
      const isSameQuery = isEqual(query.properties, serialisedProperties);

      if (isSameQuery) {
        this._cacheLimiter.markAccessed(this._queries, key);
        return query;
      }
    }
  }
}

export class AppDestroy {
  private static _onDestroy$ = new Subject<void>();
  static onDestroy$ = this._onDestroy$.asObservable();

  static destroy(): void {
    this._onDestroy$.next();
    this._onDestroy$.complete();
  }
}
